"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useMemo } from "react";
import type { Media } from "@/payload-types";
import { buildVariantLookup, type MediaVariant } from "@/utilities/media/mediaVariants";
type MediaInput = Media | number | null | undefined;
interface MediaVariantsContextValue {
  lookup: Record<string, MediaVariant[]>;
  sizes?: string;
  priority: boolean;
}
const MediaVariantsContext = createContext<MediaVariantsContextValue | null>(null);
export interface MediaVariantsProviderProps {
  /** The Media (or list of Media) whose renditions images inside this scope may resolve to. */
  media: MediaInput | MediaInput[];
  /** `sizes` attribute for images in this scope (an explicit `sizes` prop on the image wins). */
  sizes?: string;
  /** Marks images in this scope as the LCP candidate: eager load + `fetchpriority="high"`. */
  priority?: boolean;
  children: React.ReactNode;
}

/**
 * Supplies pre-generated Payload renditions to `FillImage` / `PostImage` rendered inside it,
 * so they can emit a `srcset` without the Next.js image optimizer. Images are matched by
 * their `src` url; images whose url is unknown to the scope render unchanged.
 * Nested providers merge lookups (inner wins) and the inner `sizes` / `priority` apply.
 */
export function MediaVariantsProvider(t0) {
  const $ = _c(14);
  const {
    media,
    sizes,
    priority: t1,
    children
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const parent = useContext(MediaVariantsContext);
  const t2 = parent?.lookup;
  let t3;
  if ($[0] !== media) {
    t3 = Array.isArray(media) ? media : [media];
    $[0] = media;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t3) {
    t4 = buildVariantLookup(t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t4) {
    t5 = {
      ...t2,
      ...t4
    };
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== priority || $[8] !== sizes || $[9] !== t5) {
    t6 = {
      lookup: t5,
      sizes,
      priority
    };
    $[7] = priority;
    $[8] = sizes;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const value = t6;
  let t7;
  if ($[11] !== children || $[12] !== value) {
    t7 = <MediaVariantsContext.Provider value={value}>{children}</MediaVariantsContext.Provider>;
    $[11] = children;
    $[12] = value;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

/**
 * Resolve the renditions for an image url. Returns `null` outside a provider or when the
 * url is not part of the scope's media — callers then render exactly as before.
 */
export function useMediaVariants(src) {
  const $ = _c(4);
  const scope = useContext(MediaVariantsContext);
  const variants = scope?.lookup[src];
  if (!scope || !variants) {
    return null;
  }
  let t0;
  if ($[0] !== scope.priority || $[1] !== scope.sizes || $[2] !== variants) {
    t0 = {
      variants,
      sizes: scope.sizes,
      priority: scope.priority
    };
    $[0] = scope.priority;
    $[1] = scope.sizes;
    $[2] = variants;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}